import React, { useState, useMemo } from 'react';
import {
  Card,
  CardBody,
  Table,
  Badge,
  Progress,
  Row,
  Col,
  Spinner,
  CardTitle,
  Pagination,
  PaginationItem,
  PaginationLink
} from 'reactstrap';
import { Bar, Pie } from 'react-chartjs-2';
import { Icon } from '../../../components/Component';
// Ensure Chart.js elements are registered globally

// Enhanced table styles
const tableStyles = `
  .table-modern {
    border-collapse: separate;
    border-spacing: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .table-modern thead th {
    background: #f8f9fa;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 1rem 0.75rem;
    position: relative;
  }

  .table-modern thead th:first-child {
    border-radius: 8px 0 0 0;
  }

  .table-modern thead th:last-child {
    border-radius: 0 8px 0 0;
  }

  .table-modern tbody td {
    padding: 0.5rem 0.75rem;
    font-size: 0.85rem;
  }

  .table-row-hover {
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .table-row-hover:hover {
    background-color: #f8f9fa !important;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .table-modern tbody tr:last-child td:first-child {
    border-radius: 0 0 0 8px;
  }

  .table-modern tbody tr:last-child td:last-child {
    border-radius: 0 0 8px 0;
  }

  .text-purple {
    color: #6f42c1 !important;
  }

  .progress {
    background-color: rgba(0, 0, 0, 0.1);
  }

  .badge {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .pagination-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1rem;
    padding: 0.75rem 0;
    border-top: 1px solid #e9ecef;
  }

  .pagination-info {
    font-size: 0.85rem;
    color: #6c757d;
  }

  .pagination {
    margin-bottom: 0;
  }

  .pagination .page-link {
    border: 1px solid #dee2e6;
    color: #6c757d;
    padding: 0.375rem 0.75rem;
    font-size: 0.85rem;
  }

  .pagination .page-item.active .page-link {
    background-color: #854fff;
    border-color: #854fff;
    color: white;
  }

  .pagination .page-link:hover {
    background-color: #f8f9fa;
    border-color: #dee2e6;
    color: #495057;
  }

  @media (max-width: 768px) {
    .table-modern thead th {
      font-size: 0.7rem;
      padding: 0.75rem 0.5rem;
    }

    .table-modern tbody td {
      padding: 0.5rem 0.5rem;
    }

    .pagination-wrapper {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
`;

// Helper function to determine color based on conversion rate
const getConversionRateColor = (rate) => {
  if (rate >= 50) return 'success';
  if (rate >= 25) return 'info';
  if (rate >= 10) return 'warning';
  return 'danger';
};

// Helper for chart colors
const chartColors = [
  'rgba(54, 162, 235, 0.7)',  // Blue
  'rgba(255, 99, 132, 0.7)',  // Red
  'rgba(75, 192, 192, 0.7)',  // Green/Teal
  'rgba(255, 205, 86, 0.7)',  // Yellow
  'rgba(153, 102, 255, 0.7)', // Purple
  'rgba(255, 159, 64, 0.7)', // Orange
  'rgba(201, 203, 207, 0.7)'  // Grey
];

// Helper function to truncate labels
const truncateLabel = (label, maxLength = 15) => {
  if (typeof label !== 'string') return String(label); // Ensure it's a string
  if (label.length > maxLength) {
    return label.substring(0, maxLength - 3) + "...";
  }
  return label;
};

const ChartPlaceholder = ({ message = "No data to display for this chart." }) => (
  <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '100%', minHeight: '150px', border: '1px dashed #eee', borderRadius: '4px' }}>
    <small>{message}</small>
  </div>
);

const LeadSourcePerformance = ({
  data = {},
  title = "Lead Source Performance",
  enableCharts = false,
  isLoading = false
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const leadSourcesArray = Object.entries(data).map(([sourceName, metrics]) => ({
    source: sourceName, // This is the full source name
    ...metrics
  }));

  const sortedLeadSourcesForTable = [...leadSourcesArray].sort((a, b) =>
    (b.leadsAssigned || 0) - (a.leadsAssigned || 0)
  );

  // Pagination logic
  const totalItems = sortedLeadSourcesForTable.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedLeadSourcesForTable.slice(startIndex, endIndex);
  const showPagination = totalItems > itemsPerPage;

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = useMemo(() => {
    if (totalPages === 0) return [];

    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <Card className="shadow-sm border-0">
        <CardBody className="p-3 d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner color="primary" />
        </CardBody>
      </Card>
    );
  }

  let sourceNamesForChart, metricsArrayForChart;
  let leadsAssignedDataPie, conversionRatesData, leadsWonData;
  let pieChartOptions = {}, horizontalBarChartOptions = {}, verticalBarChartOptions = {};

  let totalLeadsAssigned = 0;
  let hasAnyConversionRate = false;
  let totalLeadsWon = 0;

  const MAX_LABEL_LENGTH = 15; // Define max length for truncated labels

  if (enableCharts && leadSourcesArray.length > 0) {
    sourceNamesForChart = leadSourcesArray.map(s => s.source); // Full source names
    metricsArrayForChart = leadSourcesArray;

    totalLeadsAssigned = metricsArrayForChart.reduce((sum, s) => sum + (s.leadsAssigned || 0), 0);
    hasAnyConversionRate = metricsArrayForChart.some(s => (s.conversionRate || 0) > 0);
    totalLeadsWon = metricsArrayForChart.reduce((sum, s) => sum + (s.leadsWon || 0), 0);

    pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true, // Make sure this is true
          position: 'right',
          labels: {
            color: '#333', // Make sure color contrasts with background
            boxWidth: 10,
            padding: 10,
            font: {
              size: 10,
              weight: 'bold' // Make more visible
            },

          }
        },
        tooltip: {
          enabled: true, // Make sure tooltips are enabled
          callbacks: {
            title: function (tooltipItems) {
              return sourceNamesForChart[tooltipItems[0].dataIndex];
            }
          }
        }
      }
    };

    horizontalBarChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      // make the animation faster
      animation: {
        duration: 200
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 } },
        y: { // Source names are on Y-axis
          ticks: {
            callback: function (value) {
              // 'this.getLabelForValue(value)' gets the actual label string
              return truncateLabel(this.getLabelForValue(value), MAX_LABEL_LENGTH);
            }
          }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              // For horizontal bar, Y-axis label (source name) is the title
              // tooltipItems[0].label would be the (potentially truncated) tick label.
              // We need the original label from the chart data.
              return sourceNamesForChart[tooltipItems[0].dataIndex];
            }
          }
        }
      }
    };

    verticalBarChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      animation: {
        duration: 200
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } },
        x: { // Source names are on X-axis
          ticks: {
            callback: function (value) {
              return truncateLabel(this.getLabelForValue(value), MAX_LABEL_LENGTH);
            },
            autoSkip: false, // Prevent auto-skipping if you want all labels (truncated)
            maxRotation: 45, // Rotate if still too crowded
            minRotation: 0
          }
        }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: function (tooltipItems) {
              // For vertical bar, X-axis label (source name) is the title
              return sourceNamesForChart[tooltipItems[0].dataIndex];
            }
          }
        }
      }
    };

    if (totalLeadsAssigned > 0) {
      leadsAssignedDataPie = {
        labels: sourceNamesForChart, // Full names for tooltip reference
        datasets: [{ /* ... as before ... */ }],
      };
      leadsAssignedDataPie.datasets[0].data = metricsArrayForChart.map(s => s.leadsAssigned || 0);
      leadsAssignedDataPie.datasets[0].backgroundColor = sourceNamesForChart.map((_, i) => chartColors[i % chartColors.length]);
      leadsAssignedDataPie.datasets[0].hoverOffset = 4;
      leadsAssignedDataPie.datasets[0].borderColor = '#fff';
      leadsAssignedDataPie.datasets[0].borderWidth = 1;
    }

    if (hasAnyConversionRate) {
      conversionRatesData = {
        labels: sourceNamesForChart, // Full names for tooltip reference
        datasets: [{/* ... as before ... */ }],
      };
      conversionRatesData.datasets[0].label = 'Conversion Rate (%)';
      conversionRatesData.datasets[0].data = metricsArrayForChart.map(s => parseFloat(s.conversionRate || 0).toFixed(1));
      conversionRatesData.datasets[0].backgroundColor = sourceNamesForChart.map((_, i) => chartColors[i % chartColors.length]);
      conversionRatesData.datasets[0].borderColor = sourceNamesForChart.map((_, i) => chartColors[i % chartColors.length].replace('0.7', '1'));
      conversionRatesData.datasets[0].borderWidth = 1;
    }

    if (totalLeadsWon > 0) {
      leadsWonData = {
        labels: sourceNamesForChart, // Full names for tooltip reference
        datasets: [{ /* ... as before ... */ }],
      };
      leadsWonData.datasets[0].label = 'Leads Won';
      leadsWonData.datasets[0].data = metricsArrayForChart.map(s => s.leadsWon || 0);
      leadsWonData.datasets[0].backgroundColor = sourceNamesForChart.map((_, i) => chartColors[i % chartColors.length]);
      leadsWonData.datasets[0].borderColor = sourceNamesForChart.map((_, i) => chartColors[i % chartColors.length].replace('0.7', '1'));
      leadsWonData.datasets[0].borderWidth = 1;
    }
  }

  return (
    <>
      <style>{tableStyles}</style>
      <Card className="shadow-sm border-0" style={{ transition: 'none' }}>
        <CardBody className="p-3">
          <CardTitle tag="h5" className="mb-3" style={{ fontSize: '1rem' }}>{title}</CardTitle>

          {enableCharts && leadSourcesArray.length > 0 && (
            <Row className="g-3 mb-4">
              <Col lg="6" xl="4" style={{ minHeight: '280px', maxHeight: '320px' }}>
                <h6 className="text-muted text-center small mb-2">Leads Assigned Distribution</h6>
                {totalLeadsAssigned > 0 && leadsAssignedDataPie ?
                  <Pie data={leadsAssignedDataPie} options={pieChartOptions} /> :
                  <ChartPlaceholder />}
              </Col>
              <Col lg="6" xl="4" style={{ minHeight: '280px', maxHeight: '320px' }}>
                <h6 className="text-muted text-center small mb-2">Conversion Rate by Source (%)</h6>
                {hasAnyConversionRate && conversionRatesData ?
                  <Bar data={conversionRatesData} options={horizontalBarChartOptions} /> :
                  <ChartPlaceholder />}
              </Col>
              <Col lg="12" xl="4" style={{ minHeight: '280px', maxHeight: '320px' }}>
                <h6 className="text-muted text-center small mb-2">Leads Won by Source</h6>
                {totalLeadsWon > 0 && leadsWonData ?
                  <Bar data={leadsWonData} options={verticalBarChartOptions} /> :
                  <ChartPlaceholder />}
              </Col>
            </Row>
          )}

          {/* Enhanced Table Section */}
          {leadSourcesArray.length === 0 && !isLoading ? (
            <div className="text-center py-5">
              <div className="mb-3">
                <Icon name="bar-chart" style={{ fontSize: '3rem', opacity: 0.3, color: '#6c757d' }} />
              </div>
              <h6 className="text-muted mb-2">No Lead Source Data</h6>
              <p className="text-muted small mb-0">Lead source performance data will appear here once available</p>
            </div>
          ) : leadSourcesArray.length > 0 && (
            <div className="table-responsive">
              <Table className="table-modern align-middle mb-0">
                <thead>
                  <tr>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 ps-3" style={{ width: '25%', borderRadius: '8px 0 0 0' }}>
                      <div className="d-flex align-items-center">
                        <Icon name="layers" className="me-2 text-primary" style={{ fontSize: '0.8rem' }} />
                        Lead Source
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Icon name="users" className="me-2 text-info" style={{ fontSize: '0.8rem' }} />
                        Assigned
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Icon name="call" className="me-2 text-warning" style={{ fontSize: '0.8rem' }} />
                        Follow-ups
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Icon name="user-check" className="me-2 text-secondary" style={{ fontSize: '0.8rem' }} />
                        Prospects
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Icon name="handshake" className="me-2 text-purple" style={{ fontSize: '0.8rem' }} />
                        Deals
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <Icon name="award" className="me-2 text-success" style={{ fontSize: '0.8rem' }} />
                        Won
                      </div>
                    </th>
                    <th className="border-0 bg-light text-dark fw-semibold py-3 text-center" style={{ width: '20%', borderRadius: '0 8px 0 0' }}>
                      <div className="d-flex align-items-center justify-content-center">
                        <Icon name="percent" className="me-2 text-primary" style={{ fontSize: '0.8rem' }} />
                        Conversion Rate
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => {
                    const originalIndex = sortedLeadSourcesForTable.findIndex(originalItem => originalItem.source === item.source);
                    return (
                      <tr key={index} className="table-row-hover" style={{ borderBottom: '1px solid #f8f9fa' }}>
                        <td className="py-3 ps-3 border-0">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: '32px',
                                height: '32px',
                                backgroundColor: chartColors[originalIndex % chartColors.length],
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                color: 'white'
                              }}
                            >
                              {item.source.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <span className="fw-semibold text-dark d-block" style={{ fontSize: '0.9rem' }}>
                                {item.source}
                              </span>
                              <small className="text-muted">
                                {item.leadsAssigned > 0 ? `${((item.leadsWon || 0) / item.leadsAssigned * 100).toFixed(1)}% success` : 'No data'}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 text-end border-0">
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.9rem' }}>
                            {item.leadsAssigned || 0}
                          </span>
                        </td>
                        <td className="py-3 text-end border-0">
                          <span className="fw-medium text-dark" style={{ fontSize: '0.9rem' }}>
                            {item.leadsFollowedUp || 0}
                          </span>
                          {item.leadsAssigned > 0 && (
                            <div className="small text-muted">
                              {((item.leadsFollowedUp || 0) / item.leadsAssigned * 100).toFixed(0)}%
                            </div>
                          )}
                        </td>
                        <td className="py-3 text-end border-0">
                          <span className="fw-medium text-dark" style={{ fontSize: '0.9rem' }}>
                            {item.leadsConverted || 0}
                          </span>
                          {item.leadsFollowedUp > 0 && (
                            <div className="small text-muted">
                              {((item.leadsConverted || 0) / item.leadsFollowedUp * 100).toFixed(0)}%
                            </div>
                          )}
                        </td>
                        <td className="py-3 text-end border-0">
                          <span className="fw-medium text-dark" style={{ fontSize: '0.9rem' }}>
                            {item.listingDeals || 0}
                          </span>
                        </td>
                        <td className="py-3 text-end border-0">
                          <div className="d-flex align-items-center justify-content-end">
                            <span className="fw-bold text-success me-2" style={{ fontSize: '1rem' }}>
                              {item.leadsWon || 0}
                            </span>
                            {(item.leadsWon || 0) > 0 && (
                              <Icon name="arrow-up" className="text-success" style={{ fontSize: '0.7rem' }} />
                            )}
                          </div>
                        </td>
                        <td className="py-3 text-center border-0">
                          <div className="d-flex flex-column align-items-center">
                            <Badge
                              color={getConversionRateColor(item.conversionRate || 0)}
                              className="mb-2 px-3 py-1"
                              style={{
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                borderRadius: '12px',
                                minWidth: '60px'
                              }}
                            >
                              {parseFloat(item.conversionRate || 0).toFixed(1)}%
                            </Badge>
                            <Progress
                              value={item.conversionRate || 0}
                              color={getConversionRateColor(item.conversionRate || 0)}
                              style={{
                                height: '6px',
                                width: '60px',
                                borderRadius: '3px'
                              }}
                              className="mb-0"
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Pagination */}
              {showPagination && (
                <div className="pagination-wrapper">
                  <div className="pagination-info">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                  </div>
                  <Pagination>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink onClick={handlePrevious} style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                        <Icon name="chevron-left" style={{ fontSize: '0.8rem' }} />
                      </PaginationLink>
                    </PaginationItem>

                    {getPageNumbers.map((page, index) => (
                      <PaginationItem key={index} active={page === currentPage} disabled={page === '...'}>
                        <PaginationLink
                          onClick={() => page !== '...' && handlePageChange(page)}
                          style={{ cursor: page === '...' ? 'default' : 'pointer' }}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}

                    <PaginationItem disabled={currentPage === totalPages}>
                      <PaginationLink onClick={handleNext} style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                        <Icon name="chevron-right" style={{ fontSize: '0.8rem' }} />
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default LeadSourcePerformance;